import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Checkbox } from '@/Components/shadcn/checkbox';
import { Label } from '@/Components/shadcn/label';
import { TypographySmall } from '@/Components/shadcn';
import CurrencyAutocomplete from './CurrencyAutocomplete';
import YearlySalaryRange from './YearlySalaryRange';
import UIStrings from '@/Utils/UIStrings';
import YupValidationErrors from '@/Utils/YupValidationErrors';
import { JobDetailsFieldTypes, useJobDetailsForm } from '../JobDetailsStepUtils';
import { Currency } from '@/Models/Currency';

interface SalaryProps {
    currencies: Array<Currency>;
}

function Salary({ currencies }: SalaryProps): React.JSX.Element {
    const { control, formState } = useJobDetailsForm();
    const { errors } = formState;

    function xor(a: boolean, b: boolean): boolean {
        return (!a && b) || (a && !b);
    }

    const [customError, setCustomError] = useState<string | undefined>('');

    useEffect(() => {
        const yearlySalaryFromError = errors.yearlySalaryFrom?.message;
        const yearlySalaryToError = errors.yearlySalaryTo?.message;
        const currencyError = errors.currency?.id?.message;

        // xor(a,b) returns true if only one variable is true
        // to check if only one field has error, we use xor(xor(a, b), c)
        // using !! we convert string to boolean
        if (xor(xor(!!yearlySalaryFromError, !!yearlySalaryToError), !!currencyError)) {
            // Only 1 error
            setCustomError(yearlySalaryFromError || yearlySalaryToError || currencyError);
        } else if (yearlySalaryFromError || yearlySalaryToError || currencyError) {
            // More than 1 error
            setCustomError(YupValidationErrors.InvalidInputError);
        } else {
            // No errors
            setCustomError(undefined);
        }
    }, [errors.yearlySalaryFrom?.message, errors.yearlySalaryTo?.message, errors.currency?.id?.message]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-row gap-5">
                <YearlySalaryRange />
                <CurrencyAutocomplete currencies={currencies} />
            </div>
            {customError && <TypographySmall className="text-destructive">{customError}</TypographySmall>}
            <Controller
                name="doNotDisplaySalary"
                control={control}
                render={({ field }) => (
                    <div className="flex flex-row items-center gap-2">
                        <Checkbox
                            id="do-not-display-salary"
                            className="bg-white"
                            checked={Boolean(field.value)}
                            onCheckedChange={(v) => field.onChange(v === true)}
                            onBlur={field.onBlur}
                            data-cy={JobDetailsFieldTypes.DoNotDisplaySalary}
                        />
                        <Label htmlFor="do-not-display-salary" className="cursor-pointer font-medium">
                            {UIStrings.DoNotDisplaySalary}
                        </Label>
                    </div>
                )}
            />
        </div>
    );
}

export default Salary;
